// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ReplaceBusbarUtils } from './replace-busbar-utils';
import { Busbar } from './replace-busbar-data';
import { ReplaceDynamicArticleUtils } from '../replace-dynamic-article-utils';
import { InsertArticleHelper } from '../../insert-article.helper';
import { ArrayUtils } from '@shared/util/array-utils';
import { Dictionary } from 'typescript-collections';
import { Vector3 } from 'three';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { ArticleLineHolder } from '../../article-line-holder';
import { InsertArticleUtils } from '@project/components/article/util/insert-article-utils';
import { ArticleUsageUtils } from '../../../util/article-usage-utils';
import { InsertArticleHelperFactory } from '../../insert-article-helper.factory';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { Line } from '@3d-scene/model/line';
import { ReplaceDynamicArticleService } from '../replace-dynamic-article.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';

@Injectable({
  providedIn: 'root'
})
export class ReplaceBusbarService {

  constructor(private containerService: ContainerService,
              private articleLineHolder: ArticleLineHolder,
              private insertArticleHelperFactory: InsertArticleHelperFactory,
              private articleContainerUtils: ArticleContainerUtils,
              private replaceDynamicArticleService: ReplaceDynamicArticleService,
              private connectionInfoService: ConnectionInfoService) { }

  public getBusbarsToRecreateByUsageIds(usageIds: string[]): ArticleContainer[] {
    return this.getBusbarsToRecreateByArticles(
      this.containerService.findArticleContainersInGlobalByGuids(usageIds)
    );
  }

  public getSortedBusbarParentContainersConnectedToAcceptingId(busbar: Busbar, mountingRailInsertHelper: InsertArticleHelper): ArticleContainer[] {
    if (!busbar || !mountingRailInsertHelper) {
      return [];
    }
    const parentUsageIds: string[] = busbar.backwardAcceptingIds.map(metaElement => metaElement.articleUsageId);
    const parentContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(parentUsageIds);
    let backwardUsageIdsConnectedToRail: string[] = [];
    for (const parentContainer of parentContainers) {
      if (ReplaceDynamicArticleUtils.isArticleConnectedToAcceptingId(parentContainer, mountingRailInsertHelper.acceptingIdentity)) {
        ArrayUtils.mergeArrays(backwardUsageIdsConnectedToRail, [parentContainer.articleUsage.id]);
        continue;
      }
      const backwardUsageIds: string[] = this.replaceDynamicArticleService.getBackwardUsageIdsConnectedToAcceptingId(parentContainer, mountingRailInsertHelper.acceptingIdentity);
      ArrayUtils.mergeArrays(backwardUsageIdsConnectedToRail, backwardUsageIds);
    }
    const backwardContainersConnectedToRail: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(backwardUsageIdsConnectedToRail);
    return mountingRailInsertHelper.sortArticles(backwardContainersConnectedToRail);
  }

  public getLastBusbarParentUsageId(busbar: Busbar, mountingRailInsertHelper: InsertArticleHelper): string {
    const parentContainers: ArticleContainer[] = this.getSortedBusbarParentContainersConnectedToAcceptingId(busbar, mountingRailInsertHelper);
    return ArrayUtils.isNotEmpty(parentContainers)
      ? parentContainers[parentContainers.length - 1].articleUsage.id
      : null;
  }

  public getMaxAllowedMoveOffsetMap(busbars: Busbar[], mountingRailInsertHelper: InsertArticleHelper): Dictionary<string, number> {
    const maxAllowedMoveOffsetMap: Dictionary<string, number> = new Dictionary<string, number>();
    for (const busbar of busbars) {
      const lineStart: Vector3 = busbar.insertArticleHelper.baseLine.start.clone().add(new Vector3(-busbar.additionalLengthStart, 0, 0));
      const lineEnd: Vector3 = busbar.insertArticleHelper.baseLine.end.clone().add(new Vector3(busbar.additionalLengthEnd, 0, 0));
      const line: AdvancedLine = new AdvancedLine(lineStart, lineEnd);
      const freeLength: number = busbar.insertArticleHelper.getFreeLengthByLine(line);
      const lastBusbarParentUsageId: string = this.getLastBusbarParentUsageId(busbar, mountingRailInsertHelper);

      if (maxAllowedMoveOffsetMap.containsKey(lastBusbarParentUsageId)) {
        const previousFreeLength: number = maxAllowedMoveOffsetMap.getValue(lastBusbarParentUsageId);
        if (previousFreeLength < freeLength) {
          continue;
        }
      }
      maxAllowedMoveOffsetMap.setValue(lastBusbarParentUsageId, freeLength);
    }
    return maxAllowedMoveOffsetMap;
  }

  public getEndOverhangChildrenMoveMap(busbars: Busbar[], rangeOnLineMoveArticlesMap: Dictionary<string, Vector3>, mountingRailInsertHelper: InsertArticleHelper): Dictionary<string, Vector3> {
    const moveMap: Dictionary<string, Vector3> = new Dictionary<string, Vector3>();
    for (const busbar of busbars) {
      if (busbar.endOverhangChildrenUsageIds.length > 0) {
        const lastBusbarParentUsageId: string = this.getLastBusbarParentUsageId(busbar, mountingRailInsertHelper);
        if (lastBusbarParentUsageId && rangeOnLineMoveArticlesMap.containsKey(lastBusbarParentUsageId)) {
          const lastBusbarParentMoveOffset = rangeOnLineMoveArticlesMap.getValue(lastBusbarParentUsageId);
          for (const childUsageId of busbar.endOverhangChildrenUsageIds) {
            moveMap.setValue(childUsageId, lastBusbarParentMoveOffset.clone());
          }
        }
        this.articleLineHolder.clearArticleLineCacheByGUIDs(busbar.endOverhangChildrenUsageIds);
      }
    }
    return moveMap;
  }

  public getChildrenMoveMap(busbars: Busbar[], rangeOnLineMoveArticlesMap: Dictionary<string, Vector3>, mountingRailInsertHelper: InsertArticleHelper): Dictionary<string, Vector3> {
    const baseChildrenMoveMap: Dictionary<string, Vector3> = this.getBaseChildrenMoveMap(busbars, rangeOnLineMoveArticlesMap, mountingRailInsertHelper);
    const busbarsEndOverhangChildrenMoveMap: Dictionary<string, Vector3> = this.getEndOverhangChildrenMoveMap(busbars, rangeOnLineMoveArticlesMap, mountingRailInsertHelper);
    InsertArticleUtils.mergeMoveMap(baseChildrenMoveMap, busbarsEndOverhangChildrenMoveMap);
    return baseChildrenMoveMap;
  }

  public getBaseChildrenMoveMap(busbars: Busbar[], rangeOnLineMoveArticlesMap: Dictionary<string, Vector3>, mountingRailInsertHelper: InsertArticleHelper): Dictionary<string, Vector3> {
    const busbarsChildrenMoveMap: Dictionary<string, Vector3> = new Dictionary<string, Vector3>();
    for (const busbar of busbars) {
      const lastBusbarParentUsageId: string = this.getLastBusbarParentUsageId(busbar, mountingRailInsertHelper);
      if (!lastBusbarParentUsageId || !rangeOnLineMoveArticlesMap.containsKey(lastBusbarParentUsageId)) {
        continue;
      }
      const lastBusbarParentMoveOffset: Vector3 = rangeOnLineMoveArticlesMap.getValue(lastBusbarParentUsageId).clone();
      const baseLine: AdvancedLine = busbar.insertArticleHelper.baseLine;
      const endOverhangStart: Vector3 = baseLine.end.clone().add(new Vector3(busbar.additionalLengthEnd, 0, 0));

      let resizeLineStart: Vector3 = endOverhangStart;
      const endOverhangArticles: ArticleContainer[] = busbar.insertArticleHelper.getConnectedArticlesFromLine(new AdvancedLine(endOverhangStart, baseLine.end));
      const firstEndOverhangArticle: ArticleContainer = endOverhangArticles[0];
      if (firstEndOverhangArticle) {
        const firstEndOverhangArticleLine = busbar.insertArticleHelper.getArticleLine(firstEndOverhangArticle);
        if (baseLine.start.distanceTo(firstEndOverhangArticleLine.start) < baseLine.start.distanceTo(resizeLineStart)) {
          resizeLineStart = firstEndOverhangArticleLine.start.clone();
        }
      }
      const resizeLineEnd: Vector3 = resizeLineStart.clone().add(new Vector3(lastBusbarParentMoveOffset.x, 0, 0));

      const isShorting: boolean = baseLine.start.distanceTo(resizeLineEnd) < baseLine.start.distanceTo(resizeLineStart);
      const resizeLine: AdvancedLine = isShorting
        ? new AdvancedLine(resizeLineEnd, resizeLineStart)
        : new AdvancedLine(resizeLineStart, resizeLineEnd);

      if (isShorting) {
        const moveMap: Dictionary<string, Vector3> = busbar.insertArticleHelper.calculateMoveOffsetByArticleLine(
          resizeLine,
          null, busbar.endOverhangChildrenUsageIds,
          true, false, false, true);
        InsertArticleUtils.mergeMoveMap(busbarsChildrenMoveMap, moveMap);
      }
      const resizeOffset: number = isShorting ? resizeLine.length : -resizeLine.end;
      baseLine.end.add(new Vector3(resizeOffset, 0, 0));
    }
    return busbarsChildrenMoveMap;
  }

  public getBusbarsToRecreateByNeighborArticles(articleContainer: ArticleContainer): ArticleContainer[] {
    const backwardUsageId = ArticleUsageUtils.getFirstBackwardConnectedConnectionUsageId(articleContainer.articleUsage);
    const backwardConnection = this.connectionInfoService.findConnectionByIdentity(backwardUsageId);
    if (!ReplaceDynamicArticleUtils.isSuitableRangeOnLineConnection(backwardConnection)) {
      return [];
    }

    const insertHelper: InsertArticleHelper = this.insertArticleHelperFactory.createInstanceByAcceptingIdentity(backwardUsageId);
    if (!insertHelper) {
      return [];
    }

    const articleLine = insertHelper.getArticleLine(articleContainer);
    return !articleLine ? [] : this.findBusbarsByLine(articleLine, insertHelper);
  }

  public getBusbarsToRecreateByArticles(articleContainers: ArticleContainer[]): ArticleContainer[] {
    const busbars: ArticleContainer[] = [];
    for (const articleContainer of articleContainers) {
      const forwardConnectedContainers: ArticleContainer[] = this.getBusbarsToRecreateByNeighborArticles(articleContainer);
      ArrayUtils.mergeArrays(
        busbars,
        forwardConnectedContainers
      );
    }
    return this.filterBusbarsToRecreateByArticlesToDelete(busbars, articleContainers);
  }

  public getConnectedBusbarsToContainer(container: ArticleContainer): ArticleContainer[] {
    const forwardConnectedContainers: ArticleContainer[] = this.articleContainerUtils.getConnectedArticlesRecursively(container, true);
    return forwardConnectedContainers.filter(container => ReplaceBusbarUtils.isBusbarArticle(container.article));
  }

  public getConnectedBusbarsByArticles(articleContainers: ArticleContainer[]): ArticleContainer[] {
    const busbarArticles: ArticleContainer[] = [];
    for (const articleContainer of articleContainers) {
      const forwardConnectedContainers: ArticleContainer[] = this.getConnectedBusbarsToContainer(articleContainer);
      ArrayUtils.mergeArrays(
        busbarArticles,
        forwardConnectedContainers
      );
    }
    return busbarArticles;
  }

  public filterBusbarsToRecreateByArticlesToDelete(busbarContainers: ArticleContainer[], articlesToDelete: ArticleContainer[]): ArticleContainer[] {
    const busbarsToRecreate: ArticleContainer[] = [];
    for (const busbar of busbarContainers) {
      const backwardContainers: ArticleContainer[] = this.articleContainerUtils.getAllBackwardArticleContainerOnRailRecursively(busbar);
      if (ArrayUtils.sourceArrayContainsElementFromTargetArray(articlesToDelete, backwardContainers)) {
        continue;
      }
      busbarsToRecreate.push(busbar);
    }
    return busbarsToRecreate;
  }

  public findBusbarsByLine(line: Line, insertHelper: InsertArticleHelper): ArticleContainer[] {
    if (!line || !ReplaceDynamicArticleUtils.isSuitableInsertHelper(insertHelper)) {
      return [];
    }
    const baseLine: AdvancedLine = insertHelper.baseLine;
    const leftContainers: ArticleContainer[] = insertHelper.getConnectedArticlesFromLine(new AdvancedLine(baseLine.start, line.start));
    const rightContainers: ArticleContainer[] = insertHelper.getConnectedArticlesFromLine(new AdvancedLine(line.end, baseLine.end));

    const leftBusbars: ArticleContainer[] = this.getConnectedBusbarsByArticles(leftContainers);
    const rightBusbars: ArticleContainer[] = this.getConnectedBusbarsByArticles(rightContainers);

    if (ArrayUtils.isEmpty(leftBusbars) || ArrayUtils.isEmpty(rightBusbars)) {
      return [];
    }
    return ReplaceBusbarUtils.getCommonBusbars(leftBusbars, rightBusbars);
  }

  public findBusbarsByPoint(point: Vector3, insertHelper: InsertArticleHelper): ArticleContainer[] {
    return this.findBusbarsByLine(new Line(point, point), insertHelper);
  }

  public getBusbarsToRecreateByInsertionPoint(insertionPoint: Vector3, insertHelper: InsertArticleHelper): ArticleContainer[] {
    if (!ReplaceDynamicArticleUtils.isSuitableInsertHelper(insertHelper)) {
      return [];
    }

    return this.findBusbarsByPoint(insertionPoint, insertHelper);
  }

  public isMoveMapContainsAllBackwardUsageIdsWithSameOffset(busbar: ArticleContainer, moveArticlesMap: Dictionary<string, Vector3>, insertHelper: InsertArticleHelper): boolean {
    if (!moveArticlesMap) {
      return false;
    }
    const backwardUsageIds: string[] = this.replaceDynamicArticleService.getBackwardUsageIdsConnectedToAcceptingId(busbar, insertHelper.acceptingIdentity);
    return ReplaceBusbarUtils.isMoveMapContainsAllUsageIdsWithSameOffset(moveArticlesMap, backwardUsageIds);
  }
}
